import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, of, tap } from 'rxjs';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { Logger, ConfirmDialogComponent } from 'src/app/@shared';
import { Store, StoreService } from '../..';
import { DEFAULT_SNACKBAR_CONFIG } from 'src/app/@shared/constants/site.constants';
import { BannersService } from '../../services/banners.service';
import { Banner } from '../../models/banner.model';
import { StoreDomain } from '../../models/store-domain.model';
import { StoreAttributeService } from '../../services/store-attribute.service';
import { coerceAttributeFieldValue, buildAttributeFieldsPayload, extractAttributeFieldValues, mapAttributePayloadToClientValues, resolveClientKeyForAttributePayloadKey, resolveStoreSourceFieldValue, StoreAttributeEditCategoryViewModel, StoreSourceBindingContext } from '../../models/store-attribute.model';

const log = new Logger('StoreComponent');

@Component({
  selector: 'app-store',
  templateUrl: './store.component.html',
  styleUrls: ['./store.component.scss']
})

export class StoreComponent<T extends Store, TStoreDomain extends StoreDomain<Store>> implements OnInit {
  store$: Observable<StoreDomain<T>> = of({} as StoreDomain<T>);
  storeAttributesCategories$: Observable<StoreAttributeEditCategoryViewModel[]> = of([]);
  banners$: any[] = [] as any[];
  divisions$: any[] = [] as any[];
  private attributeFieldValues: Record<string, unknown> = {};
  private rawAttributeFieldPayload: Record<string, unknown> = {};
  private attributeCategories: StoreAttributeEditCategoryViewModel[] = [];
  private loadedStoreDomain: StoreDomain<Store> | null = null;

  formGroup = new FormGroup({
    Detail: new FormGroup({
    ClientKey: new FormControl<string>('', [Validators.required, Validators.minLength(1), Validators.maxLength(100),]),
    Id: new FormControl({ value: '', disabled: true }, { nonNullable: true }),
    StoreName: new FormControl<string>('', [Validators.required, Validators.minLength(1), Validators.maxLength(100),]),
    Address1: new FormControl<string>('', [Validators.minLength(1), Validators.maxLength(255),]),
    Address2: new FormControl<string>('', [Validators.minLength(1), Validators.maxLength(255),]),
    BannerId : new FormControl<string>(''),
    DivisionId : new FormControl<string>(''),
    City: new FormControl<string>('', [Validators.minLength(1), Validators.maxLength(100), Validators.pattern("[a-zA-Z0-9 ]*")]),
    State: new FormControl<string>('', [Validators.minLength(1), Validators.maxLength(25), Validators.pattern("[a-zA-Z0-9 ]*")]),
    PostalCode: new FormControl<string>('', [Validators.minLength(1), Validators.maxLength(100), Validators.pattern("[a-zA-Z0-9]*")]),
    AttributeFields: new FormGroup({}),
    }),
  });
  storeId: string = '0';

  private readonly sourceBindingDetailFields = ['City', 'State', 'BannerId', 'DivisionId'] as const;

  constructor(private router: Router,
    private route: ActivatedRoute,
    private storeService: StoreService<T, TStoreDomain>,
    private storeAttributeService: StoreAttributeService,
    private matSnackBar: MatSnackBar,
    private bannersService: BannersService<Banner>,
    private dialog: MatDialog) { }

  ngOnInit(): void {
    log.debug('init');
    this.storeId = this.route.snapshot.params['storeId'];
    this.loadStoreAttributes();
    this.subscribeToSourceBindingFields();

    this.storeService.getBanners().subscribe({
      next: (data: any) => {
        this.banners$ = data.value;
        this.applySourceFieldBindings();
      }
    });
    this.storeService.getDivisions().subscribe({
      next: (data: any) => {
        this.divisions$ = data.value;
        this.applySourceFieldBindings();
      }
    });

    if (this.storeId && this.storeId !== '0') {
      this.store$ = this.storeService.getStore(this.storeId).pipe(
        tap((store) => {
          this.loadedStoreDomain = store;
          this.rawAttributeFieldPayload = extractAttributeFieldValues(store.Detail);
          this.attributeFieldValues = mapAttributePayloadToClientValues(
            this.rawAttributeFieldPayload,
            this.attributeCategories,
          );
          const { AttributeFields, BannerId, DivisionId, ...detailRest } = store.Detail ?? {};
          this.formGroup.patchValue({ Detail: detailRest });
          this.formGroup.controls.Detail.controls.BannerId.patchValue(store.BannerId as string);
          this.formGroup.controls.Detail.controls.DivisionId.patchValue(store.DivisionId as string);
          this.hydrateAttributeFields();
          this.formGroup.markAllAsTouched();
        })
      );
    }
  }

  getAttributeFieldsGroup(): FormGroup {
    return this.formGroup.controls.Detail.controls.AttributeFields as FormGroup;
  }

  private loadStoreAttributes(): void {
    this.storeAttributeService.getStoreAttributesEditViewModel().subscribe({
      next: (categories) => {
        this.attributeCategories = categories;
        this.storeAttributesCategories$ = of(categories);
        this.buildAttributeFieldControls(categories);
        this.attributeFieldValues = mapAttributePayloadToClientValues(
          this.rawAttributeFieldPayload,
          categories,
        );
        this.hydrateAttributeFields();
      },
    });
  }

  private subscribeToSourceBindingFields(): void {
    this.sourceBindingDetailFields.forEach((fieldName) => {
      this.formGroup.controls.Detail.get(fieldName)?.valueChanges.subscribe(() => {
        this.applySourceFieldBindings();
      });
    });
  }

  private hydrateAttributeFields(): void {
    this.patchStoredAttributeFieldValues();
    this.applySourceFieldBindings();
  }

  private buildAttributeFieldControls(categories: StoreAttributeEditCategoryViewModel[]): void {
    const group = this.getAttributeFieldsGroup();

    categories.forEach((category) => {
      category.fields.forEach((field) => {
        if (group.get(field.clientKey)) {
          return;
        }

        const defaultValue = field.valueType === 'Boolean' ? false : field.valueType === 'MultiSelect' ? [] : null;

        const control = new FormControl({
          value: defaultValue,
          disabled: field.isReadOnly,
        });

        group.addControl(field.clientKey, control);
      });
    });
  }

  private getBindingContext(): StoreSourceBindingContext {
    const detail = this.formGroup.controls.Detail.getRawValue() as Store;
    const banner = this.banners$?.find((item: Banner) => item.Id === detail.BannerId);
    const division = this.divisions$?.find((item: any) => item.Id === detail.DivisionId);

    return {
      Detail: { ...detail } as Record<string, unknown>,
      BannerId: detail.BannerId ?? this.loadedStoreDomain?.BannerId,
      DivisionId: detail.DivisionId ?? this.loadedStoreDomain?.DivisionId,
      BannerName: banner?.BannerName ?? this.loadedStoreDomain?.BannerName,
      DivisionName: division?.DivisionName ?? this.loadedStoreDomain?.DivisionName,
      VersionName: this.loadedStoreDomain?.VersionName,
    };
  }

  private applySourceFieldBindings(): void {
    if (!this.attributeCategories.length) {
      return;
    }

    const group = this.getAttributeFieldsGroup();
    const bindingContext = this.getBindingContext();

    this.attributeCategories.forEach((category) => {
      category.fields.forEach((field) => {
        if (!field.sourceField) {
          return;
        }

        const control = group.get(field.clientKey);
        if (!control) {
          return;
        }

        let value = resolveStoreSourceFieldValue(
          field.sourceField,
          bindingContext,
          this.banners$,
          this.divisions$,
        );

        if (value === undefined) {
          value = this.attributeFieldValues[field.clientKey];
        }

        control.setValue(
          coerceAttributeFieldValue(value, field.valueType, field.options),
          { emitEvent: false },
        );
      });
    });
  }

  private patchStoredAttributeFieldValues(): void {
    if (!this.attributeCategories.length) {
      return;
    }

    const group = this.getAttributeFieldsGroup();

    this.attributeCategories.forEach((category) => {
      category.fields.forEach((field) => {
        if (field.sourceField) {
          return;
        }

        const control = group.get(field.clientKey);
        if (!control || !(field.clientKey in this.attributeFieldValues)) {
          return;
        }

        control.setValue(
          coerceAttributeFieldValue(
            this.attributeFieldValues[field.clientKey],
            field.valueType,
            field.options,
          ),
          { emitEvent: false },
        );
      });
    });
  }

  private getSourceMappedClientKeys(): Set<string> {
    const keys = new Set<string>();

    this.attributeCategories.forEach((category) => {
      category.fields.forEach((field) => {
        if (field.sourceField) {
          keys.add(field.clientKey);
        }
      });
    });

    return keys;
  }

  trimControlValues(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key: string) => {
      const abstractControl = formGroup.get(key);
      if (abstractControl instanceof FormGroup) {
        this.trimControlValues(abstractControl);
      } else {
        if (typeof abstractControl?.value == 'string') {
          abstractControl.setValue(abstractControl?.value.trim());
        }
      }
    })
  }
  
  save() {
    this.trimControlValues(this.formGroup);
    if (this.formGroup.valid) {
      let request = this.formGroup.getRawValue() as StoreDomain<Store>;
      request.BannerId = this.formGroup.controls.Detail.controls.BannerId.value as string;
      delete request.Detail.BannerId;
      request.DivisionId = this.formGroup.controls.Detail.controls.DivisionId.value as string;
      delete request.Detail.DivisionId;

      request.Detail.AttributeFields = buildAttributeFieldsPayload(
        this.getAttributeFieldsGroup().getRawValue() as Record<string, unknown>,
        this.attributeCategories,
        this.getSourceMappedClientKeys(),
      );
      this.storeService.saveStore(request).subscribe({
        next: (response) => {
          this.matSnackBar.open(
            `${this.formGroup.controls.Detail.controls.StoreName.value} saved`, 'OK', DEFAULT_SNACKBAR_CONFIG
          );
          this.storeService.reload();
          this.router.navigate([`../`], { relativeTo: this.route, queryParamsHandling: 'preserve' });
        },
        error: (error) => {
          if (error.status === 500) {
            log.error('500 Error saving event', error);
          }
          if (error.status === 400) {
            const apiValidations: any = error.error;
            if (Array.isArray(apiValidations)) {
              apiValidations.forEach((validation: any) => {
                const attributeKey = validation.PropertyName?.startsWith('AttributeFields.')
                  ? validation.PropertyName.replace('AttributeFields.', '')
                  : null;
                const clientKey = attributeKey
                  ? resolveClientKeyForAttributePayloadKey(attributeKey, this.attributeCategories) ?? attributeKey
                  : null;
                const attributeControl = clientKey
                  ? this.formGroup.get(`Detail.AttributeFields.${clientKey}`)
                  : null;
                const control = attributeControl ?? this.formGroup?.get(validation.PropertyName);

                if (control) {
                  control.markAsTouched();
                  control.setErrors({ invalid: validation.ErrorMessage });
                  this.matSnackBar.open(validation.ErrorMessage, 'OK', { verticalPosition: 'top', panelClass: ['snackbar-error'] });
                } else {
                  this.matSnackBar.open(validation.ErrorMessage, 'OK', { verticalPosition: 'top', panelClass: ['snackbar-error'] });
                  ///TODO: if we have cross field validation then show the validation error at the top of the screen
                  // if we have cross field validation then show the validation error at the top of the screen
                  // push general error messages to array this is displayed in a toast or dialog
                }
              });
            } else {
              this.matSnackBar.open(apiValidations, 'Error', { verticalPosition: 'top', panelClass: ['snackbar-error'] });
            }


          }
        }
      });
    }
  }

  cancel(): void {
    this.formGroup.reset();
    this.router.navigate([`../`], { relativeTo: this.route, queryParamsHandling: 'preserve' });
  }

  deleteRecord() {
    const record = this.formGroup.getRawValue();
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Confirm Delete',
        message: `Are you sure you wish to delete ${record.Detail.StoreName}?`,
      },
      disableClose: true,
    });

    confirmDialog.afterClosed().subscribe(
      confirmResult => {
        if (confirmResult) {
          this.storeService.deleteStore(record.Detail.Id).subscribe({
            next: () => {
              this.matSnackBar.open(`${record.Detail.StoreName} deleted`, 'OK', DEFAULT_SNACKBAR_CONFIG);
              this.storeService.reload();
              this.router.navigate([`../`], { relativeTo: this.route, queryParamsHandling: 'preserve' });
            },
            error: (error) => {
              log.error('Error deleting event', error);

              if (error.error.value) {
                throw new Error(error.error.value);
              } else {
                throw new Error(error.message);
              }
            }
          });
        }
      });
  }

}
